import StatTile from "@/components/company/shared/stat-tile";
import { Badge } from "@/components/ui/badge";
import BlurInText from "@/components/ui/blur-in-text";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import SourceHostLink from "@/components/company/shared/source-host-link";
import { Skeleton } from "@/components/ui/skeleton";
import { useTranslation } from "@/hooks/useTranslation";
import {
  KEY_FIGURE_TILES,
  KEY_FIGURES_GRID_CLASS,
  webFigureOf,
  webFigureText,
  type KeyFigureId,
  type KeyFigureTile,
  type TagPlace,
  type WebFigure,
} from "@/lib/company-web-figures";
import { sourceLink } from "@/lib/source-link";
import { cn } from "@/lib/utils";
import type { CompanyWebFigure, CompanyWebKeyData } from "@shaarp/common";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type PointerEvent,
} from "react";

const VALUE_CLASS = "block whitespace-normal break-words";
const TILE_SKELETON_CLASS = "h-[66px] rounded-xl";
// Grace before the basis closes: lets the pointer cross the gap onto it.
const BASIS_CLOSE_DELAY_MS = 150;

/**
 * "AI estimate" marker. The basis of the estimate opens on hover, on keyboard
 * focus and on tap: a popover rather than a tooltip, because a tooltip never
 * opens on touch and the basis must be reachable on mobile. Opened by hover, it
 * leaves the focus where it was (the chat composer) and stays open while the
 * pointer is over it.
 */
function EstimatedMarker({ basis }: { basis?: string }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelClose = useCallback(() => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
  }, []);
  const scheduleClose = useCallback(() => {
    cancelClose();
    closeTimerRef.current = setTimeout(
      () => setOpen(false),
      BASIS_CLOSE_DELAY_MS,
    );
  }, [cancelClose]);
  useEffect(() => cancelClose, [cancelClose]);

  const openOnHover = (event: PointerEvent<HTMLElement>) => {
    if (event.pointerType !== "mouse") return;
    cancelClose();
    setOpen(true);
  };
  const closeOnLeave = (event: PointerEvent<HTMLElement>) => {
    if (event.pointerType === "mouse") scheduleClose();
  };

  const badge = (
    <Badge
      variant="outline"
      className="rounded-full border-majorelle/20 bg-majorelle/10 text-majorelle dark:text-violet-300"
    >
      {t("company.webPreview.figures.estimated")}
    </Badge>
  );
  if (!basis) return badge;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          aria-label={t("company.webPreview.figures.basisLabel")}
          onPointerEnter={openOnHover}
          onPointerLeave={closeOnLeave}
          className="relative z-10 -ml-1 mt-0.5 h-auto self-start px-1 py-0.5 hover:bg-transparent max-sm:min-h-11"
        >
          {badge}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        side="top"
        className="w-64 p-3 text-xs"
        onOpenAutoFocus={(event) => event.preventDefault()}
        onPointerEnter={openOnHover}
        onPointerLeave={closeOnLeave}
      >
        {t("company.webPreview.figures.basis", { basis })}
      </PopoverContent>
    </Popover>
  );
}

function FigureTile({
  tile,
  figure,
  isWriting,
}: {
  tile: KeyFigureTile;
  figure: WebFigure;
  /** The tile blurs in as it replaces its placeholder. */
  isWriting: boolean;
}) {
  const { t } = useTranslation();

  if (figure === undefined) {
    return <Skeleton className={TILE_SKELETON_CLASS} aria-hidden />;
  }
  // A figure with its estimate flag, source and basis; null for plain text.
  const structured = typeof figure === "object" ? figure : null;
  const source =
    structured?.url && !structured.estimated ? sourceLink(structured.url) : null;
  return (
    <StatTile
      className={isWriting ? "blur-in-appear" : undefined}
      icon={tile.icon}
      tone={tile.tone}
      label={t(tile.labelKey)}
      value={<FigureValue figure={figure} isWriting={isWriting} />}
      footer={
        structured?.estimated ? <EstimatedMarker basis={structured.basis} /> : undefined
      }
      href={source?.href}
      hrefLabel={
        source
          ? t("company.webPreview.figures.source", { host: source.host })
          : undefined
      }
    />
  );
}

/**
 * Stated: the value. Estimated: "~ value". Absent: "Not found". A plain string
 * blurs in a word at a time while it is written.
 */
function FigureValue({
  figure,
  isWriting,
}: {
  figure: Exclude<WebFigure, undefined>;
  isWriting: boolean;
}) {
  const { t } = useTranslation();
  if (!figure) {
    return (
      <span className={cn(VALUE_CLASS, "text-sm font-medium text-muted-foreground")}>
        {t("company.webPreview.figures.missing")}
      </span>
    );
  }
  if (typeof figure === "string") {
    return (
      <span className={VALUE_CLASS}>
        <BlurInText text={figure} isWriting={isWriting} />
      </span>
    );
  }
  return (
    <span className={VALUE_CLASS}>
      {webFigureText(figure)}
    </span>
  );
}

/**
 * What backs a web figure shown among a company's own: the "AI estimate"
 * marker with its basis, or the page that states it. Nothing for a stated
 * figure whose page is not a link.
 */
export function CompanyWebFigureNote({ figure }: { figure: CompanyWebFigure }) {
  if (figure.estimated) return <EstimatedMarker basis={figure.basis} />;
  const source = figure.url ? sourceLink(figure.url) : null;
  return source ? (
    <SourceHostLink link={source} className="inline-flex max-w-full text-xs" />
  ) : null;
}

/** Placeholder tiles while the run is still reading pages. */
export function CompanyWebFiguresSkeleton() {
  return (
    <div className={KEY_FIGURES_GRID_CLASS} aria-hidden>
      {KEY_FIGURE_TILES.map((tile) => (
        <Skeleton key={tile.id} className={TILE_SKELETON_CLASS} />
      ))}
    </div>
  );
}

/**
 * The four key figures of the web profile, in the tiles of the company panel.
 * Stated: the value, the whole tile links to its source. Estimated: "~ value"
 * and an "Estimated" marker with its basis. Absent: "Not found". Not written
 * yet, while the profile streams: a placeholder tile, and the tile blurs in
 * once written.
 *
 * `tag` places the company when the web gave no location.
 */
export default function CompanyWebFigures({
  keyData,
  tag,
  isWriting = false,
}: {
  keyData: Partial<CompanyWebKeyData>;
  tag?: TagPlace | null;
  isWriting?: boolean;
}) {
  return (
    <div className={KEY_FIGURES_GRID_CLASS}>
      {KEY_FIGURE_TILES.map((tile) => (
        <FigureTile
          key={tile.id}
          tile={tile}
          figure={webFigureOf(tile.id, keyData, tag)}
          isWriting={isWriting}
        />
      ))}
    </div>
  );
}

/**
 * The tile of one key figure as the web states it, for the panel of a company
 * already in base to show among its own tiles where it lacks the figure.
 */
export function CompanyWebFigureTile({
  id,
  keyData,
  tag,
  isWriting = false,
}: {
  id: KeyFigureId;
  keyData: Partial<CompanyWebKeyData>;
  tag?: TagPlace | null;
  isWriting?: boolean;
}) {
  const tile = KEY_FIGURE_TILES.find((spec) => spec.id === id);
  if (!tile) return null;
  return (
    <FigureTile
      tile={tile}
      figure={webFigureOf(id, keyData, tag)}
      isWriting={isWriting}
    />
  );
}
